
import React, { useState } from 'react';
import { Code, Database, Box, Info, AlertTriangle } from 'lucide-react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData } from '../../../types';
import { VariableDefinition } from '../../../utils/variableExtraction';

interface ScriptTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
    context?: VariableDefinition[];
    nodeType?: string;
}

export const ScriptTab: React.FC<ScriptTabProps> = ({ data, onChange, context = [], nodeType }) => {
    const [showDebug, setShowDebug] = useState(false);

    // Filter out duplicates if any (simple name check)
    const uniqueContext = context.filter((v, i, self) => 
        i === self.findIndex((t) => t.name === v.name)
    );

    // Separate System vs User vars
    const systemVars = uniqueContext.filter(v => v.sourceNodeId === 'system');
    const userVars = uniqueContext.filter(v => v.sourceNodeId !== 'system');

    // Group user variables by Source Node
    const groupedUserVars = userVars.reduce((acc, v) => {
        const key = v.sourceNodeLabel || v.sourceNodeId;
        if (!acc[key]) acc[key] = [];
        acc[key].push(v);
        return acc;
    }, {} as Record<string, VariableDefinition[]>);

    return (
        <div className="flex h-full flex-col">
            <div className="p-6 pb-2 shrink-0">
                <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 flex gap-3 mb-6">
                    <Code className="text-slate-500 dark:text-slate-400 mt-0.5 shrink-0" size={16} />
                    <div className="space-y-1">
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Script Execution</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                            Execute custom JavaScript. You must <code>return</code> an object where keys map to output variables.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mb-4">
                    <PropertyField 
                        config={{ 
                            name: 'scriptLanguage', 
                            label: 'Language', 
                            type: 'select', 
                            options: [
                                { value: 'javascript', label: 'JavaScript' },
                                { value: 'python', label: 'Python (Experimental)' }
                            ], 
                            defaultValue: 'javascript' 
                        }}
                        value={data.scriptLanguage}
                        onChange={onChange}
                    />
                </div>
            </div>

            <div className="flex-1 min-h-0 flex px-6 pb-6 gap-6">
                {/* Main Script Editor */}
                <div className="flex-1 flex flex-col">
                    <div className="flex-1 relative group h-full">
                        <div className="absolute top-0 right-0 p-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 px-2 py-1 rounded border border-slate-200 dark:border-slate-700">
                                JS
                            </span>
                        </div>
                        <PropertyField 
                            config={{ 
                                name: 'script', 
                                label: 'Script Code', 
                                type: 'textarea', 
                                rows: 20, 
                                placeholder: '// Example:\nconst total = variables.price * variables.qty;\n\n// Return object to update variables\nreturn {\n  total: total,\n  isValid: total > 0\n};',
                                description: 'ES6 Syntax. Use return { key: value } to output data.'
                            }}
                            value={data.script}
                            onChange={onChange}
                        />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                        <PropertyField 
                            config={{ 
                                name: 'inputVariables', 
                                label: 'Input Variables (Read)', 
                                type: 'array', 
                                itemType: 'text' 
                            }}
                            value={data.inputVariables}
                            onChange={onChange}
                        />
                        <PropertyField 
                            config={{ 
                                name: 'outputVariables', 
                                label: 'Output Variables (Write)', 
                                type: 'array', 
                                itemType: 'text' 
                            }}
                            value={data.outputVariables}
                            onChange={onChange}
                        />
                    </div>
                </div>

                {/* Context Sidebar */}
                <div className="w-64 shrink-0 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden flex flex-col">
                    <div className="p-3 border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-950 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Database size={14} className="text-slate-500"/>
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Variables</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button 
                                onClick={() => setShowDebug(!showDebug)} 
                                className="p-1 text-slate-400 hover:text-slate-600 rounded"
                                title="Toggle Debug Info"
                            >
                                <Info size={12} />
                            </button>
                            <span className="text-[9px] bg-slate-200 dark:bg-slate-800 text-slate-500 px-1.5 rounded-full">{context.length}</span>
                        </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-2 space-y-4">
                        {showDebug && (
                            <div className="p-2 mb-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded text-[10px] text-amber-800 dark:text-amber-300 font-mono break-all">
                                <div><strong>DEBUG INFO:</strong></div>
                                <div>Context Size: {context.length}</div>
                                <div>NodeType: {nodeType}</div>
                            </div>
                        )}

                        {context.length === 0 && (
                            <div className="p-4 text-center">
                                <AlertTriangle className="mx-auto text-amber-400 mb-2" size={20} />
                                <div className="text-[10px] text-slate-400 italic mb-2">No variables found.</div>
                                <div className="text-[9px] text-slate-300">
                                    Ensure this node is connected to upstream nodes that generate data (e.g., Forms, APIs).
                                </div>
                            </div>
                        )}

                        {/* User Variables */}
                        {Object.entries(groupedUserVars).map(([source, vars]) => (
                            <div key={source} className="space-y-1">
                                <div className="px-1 text-[10px] font-bold text-teal-600 dark:text-teal-400 uppercase tracking-wider truncate border-b border-slate-100 dark:border-slate-800 pb-1 mb-1">
                                    {source}
                                </div>
                                {(vars as VariableDefinition[]).map((v, idx) => (
                                    <div key={idx} className="p-2 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg group hover:border-teal-500 transition-colors cursor-help shadow-sm">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-1.5 overflow-hidden">
                                                <Database size={10} className="text-teal-500"/>
                                                <span className="text-xs font-mono font-bold text-slate-700 dark:text-slate-300 truncate" title={v.name}>{v.name}</span>
                                            </div>
                                            <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-slate-500 uppercase shrink-0 font-medium">{v.type}</span>
                                        </div>
                                        {v.description && (
                                            <div className="mt-1 pt-1 border-t border-slate-50 dark:border-slate-900 text-[9px] text-slate-400 leading-tight truncate">
                                                {v.description}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}

                        {/* System Variables (Bottom) */}
                        {systemVars.length > 0 && (
                            <div className="space-y-1 pt-2 border-t border-slate-200 dark:border-slate-800">
                                <div className="px-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">
                                    System
                                </div>
                                {systemVars.map((v, idx) => (
                                    <div key={idx} className="p-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-lg opacity-75 hover:opacity-100 transition-opacity">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-1.5 overflow-hidden">
                                                <Box size={10} className="text-slate-400"/>
                                                <span className="text-xs font-mono font-bold text-slate-600 dark:text-slate-400 truncate" title={v.name}>{v.name}</span>
                                            </div>
                                            <span className="text-[9px] px-1.5 py-0.5 bg-white dark:bg-slate-950 rounded text-slate-400 uppercase shrink-0 font-medium border border-slate-100 dark:border-slate-800">{v.type}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
